import {
  agentsApiSchemas,
  authApiSchemas,
  completionsApiSchemas,
} from '@kanbu/schema/contracts';
import ky, { type Options } from '@toss/ky';

import {
  type TypedClientFactoryOptions,
  createTypedKyClient,
  type ApiDefinition,
} from '../lib/kyClientFactory';

/**
 * AI Core API definition.
 *
 * This should in the future replace the one
 * in the chatbot-app and some definitions in the web-input. So stuff
 * can be shared across apps like playground, chatbot-app, web-input, etc.
 */
const aiCoreApiDefinition = {
  auth: {
    register: {
      resend: {
        path: authApiSchemas.register.resend.path,
        method: authApiSchemas.register.resend.method,
        input: authApiSchemas.register.resend.schema.body.merge(
          authApiSchemas.register.resend.schema.params,
        ),
        output: authApiSchemas.register.resend.schema.response[200],
      },
    },
  },
  agents: {
    summary: {
      path: agentsApiSchemas.summary.path,
      method: agentsApiSchemas.summary.method,
      input: agentsApiSchemas.summary.schema.params,
      output: agentsApiSchemas.summary.schema.response[200],
    },
    update: {
      path: agentsApiSchemas.update.path,
      method: agentsApiSchemas.update.method,
      input: agentsApiSchemas.update.schema.params.merge(
        agentsApiSchemas.update.schema.body,
      ),
    },
  },
  completions: {
    config: {
      path: completionsApiSchemas.completions.config.path,
      method: completionsApiSchemas.completions.config.method,
      input: completionsApiSchemas.completions.config.schema.params,
      output: completionsApiSchemas.completions.config.schema.response[200],
    },
    callouts: {
      path: completionsApiSchemas.completions.callouts.path,
      method: completionsApiSchemas.completions.callouts.method,
      input: completionsApiSchemas.completions.callouts.schema.params.merge(
        completionsApiSchemas.completions.callouts.schema.body,
      ),
      output: completionsApiSchemas.completions.callouts.schema.response[200],
    },
  },
} as const satisfies ApiDefinition;

/**
 * Factory function to create a typed ky client for the AI Core API.
 *
 * You need to provide your own ky instance options, which allows you to define
 * custom hooks, prefix url, etc.
 */
export function createAiCoreClient(
  kyOptions: Options,
  factoryOptions?: TypedClientFactoryOptions,
) {
  const kyInstance = ky.create({
    throwHttpErrors: true,
    ...kyOptions,
  });

  return createTypedKyClient({
    client: kyInstance,
    apiDefinition: aiCoreApiDefinition,
    options: factoryOptions,
  });
}
